import React, { useEffect, useState } from "react";
import { ArrowLeft, Sparkles, ArrowRight, Download } from "lucide-react";
import GuideWA from "@/pages/GuideWA";
import GuideLeadModal from "@/components/guide/GuideLeadModal";

const L = {
  en: {
    back: "Back to AussieCV",
    cta_title: "Ready to apply?",
    cta_sub: "Optimize your CV for Aussie ATS · 60 sec · Free",
    cta_btn: "Create my CV",
    tip: "TIP",
    tip_msg: "Your French CV won't pass Australian ATS filters.",
  },
  fr: {
    back: "Retour à AussieCV",
    cta_title: "Prêt à postuler ?",
    cta_sub: "Optimise ton CV pour l'ATS australien · 60 sec · Gratuit",
    cta_btn: "Créer mon CV",
    tip: "ASTUCE",
    tip_msg: "Ton CV français ne passera pas les filtres ATS australiens.",
  },
  de: {
    back: "Zurück zu AussieCV",
    cta_title: "Bereit zu bewerben?",
    cta_sub: "Optimiere deinen CV für australische ATS · 60 Sek · Gratis",
    cta_btn: "CV erstellen",
    tip: "TIPP",
    tip_msg: "Dein französischer CV wird die australischen ATS-Filter nicht passieren.",
  },
  it: {
    back: "Torna a AussieCV",
    cta_title: "Pronto a candidarti?",
    cta_sub: "Ottimizza il tuo CV per l'ATS australiano · 60 sec · Gratis",
    cta_btn: "Crea il mio CV",
    tip: "SUGGERIMENTO",
    tip_msg: "Il tuo CV francese non passerà i filtri ATS australiani.",
  },
  es: {
    back: "Volver a AussieCV",
    cta_title: "¿Listo para postular?",
    cta_sub: "Optimiza tu CV para el ATS australiano · 60 seg · Gratis",
    cta_btn: "Crear mi CV",
    tip: "CONSEJO",
    tip_msg: "Tu CV francés no pasará los filtros ATS australianos.",
  },
  pt: {
    back: "Voltar ao AussieCV",
    cta_title: "Pronto para candidatar?",
    cta_sub: "Otimiza o teu CV para o ATS australiano · 60 seg · Grátis",
    cta_btn: "Criar o meu CV",
    tip: "DICA",
    tip_msg: "O teu CV francês não passará os filtros ATS australianos.",
  },
};

export default function GuideWAWrapper({ lang = "en", onBackHome, onOpenCV, user }) {
  const t = L[lang] || L.en;
  const [leadModalOpen, setLeadModalOpen] = useState(false);

  // Intercept the guide's built-in print/download action so we always
  // capture the lead first, then serve a real PDF from the backend.
  useEffect(() => {
    const original = window.print;
    window.print = () => setLeadModalOpen(true);
    return () => { window.print = original; };
  }, []);

  const openDownload = () => setLeadModalOpen(true);

  return (
    <div className="min-h-screen bg-[#FAF6F0]" data-testid="guide-wa-wrapper">
      {/* Top toolbar — persistent, ties guide back to main site */}
      <div className="sticky top-0 z-50 bg-white border-b border-[#EAE5DB] shadow-sm" data-testid="guide-wa-toolbar">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 h-12 sm:h-14 flex items-center justify-between gap-2">
          <button
            onClick={onBackHome}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-[#292524] hover:bg-[#FAF6F0] transition-colors text-xs sm:text-sm font-semibold min-w-0"
            data-testid="guide-wa-back-btn"
            aria-label={t.back}
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t.back}</span>
          </button>
          {/* Persistent small CTA (2c) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={openDownload}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-white border border-[#EAE5DB] hover:border-[#D95D39] text-[#292524] text-[11px] sm:text-xs font-bold whitespace-nowrap transition-colors"
              data-testid="guide-wa-toolbar-download-btn"
              title="Download PDF"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button
              onClick={onOpenCV}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-[#D95D39] hover:bg-[#C24F2E] text-white text-[11px] sm:text-xs font-bold whitespace-nowrap shadow-sm transition-colors"
              data-testid="guide-wa-toolbar-cv-btn"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="hidden xs:inline sm:inline">{t.cta_btn}</span>
            </button>
          </div>
        </div>
        {/* Thin tip bar (2c) — informational nudge */}
        <div className="bg-[#FDF7E6] border-t border-[#E5A93D]/30 px-3 sm:px-4 lg:px-8 py-1.5 text-[10px] sm:text-xs text-[#8B6A1E] flex items-center justify-center gap-2 max-w-7xl mx-auto">
          <span className="inline-block px-1.5 py-0.5 rounded bg-[#E5A93D] text-white text-[9px] sm:text-[10px] font-bold leading-tight flex-shrink-0">{t.tip}</span>
          <span className="truncate">{t.tip_msg}</span>
        </div>
      </div>

      {/* The guide content itself — untouched */}
      <div className="pb-24">
        <GuideWA />
      </div>

      {/* Sticky bottom CTA bar (2b + 2d) — always visible, aggressive conversion */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-[#D95D39] via-[#C24F2E] to-[#9B3B1E] shadow-2xl border-t-2 border-[#9B3B1E]"
        data-testid="guide-wa-bottom-cta"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-heading font-extrabold text-white text-sm sm:text-base leading-tight">
              {t.cta_title}
            </div>
            <div className="text-white/85 text-[10px] sm:text-xs truncate">
              {t.cta_sub}
            </div>
          </div>
          <button
            onClick={onOpenCV}
            className="inline-flex items-center gap-1.5 bg-white hover:bg-[#FAF6F0] text-[#292524] font-bold rounded-xl px-3 sm:px-5 py-2 sm:py-3 text-xs sm:text-sm shadow-lg whitespace-nowrap flex-shrink-0 transition-colors"
            data-testid="guide-wa-bottom-cta-btn"
          >
            <Sparkles className="w-4 h-4 text-[#D95D39]" />
            {t.cta_btn}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <GuideLeadModal
        open={leadModalOpen}
        onClose={() => setLeadModalOpen(false)}
        lang={lang}
        prefill={{ email: user?.email || "", name: user?.name || "" }}
      />
    </div>
  );
}
